import { HOSPITAL_CARDS_LABELS } from '../types/hospital.type';
import { OVERALL_CARDS_LABELS } from '../types/overall.type';
import { DECEASED_CARDS_LABELS } from '../types/deceased.type';
import { ADMISSION_CARDS_LABELS } from '../types/admission.type';
import { COMMUNITY_CARD_LABELS } from '../types/community-transfer.type';

/**
 * Unified map of every card-id → user-facing label we know about across the 5
 * ADT pages. Used as a fallback when no runtime title/label is attached to the
 * card object (older saved layouts, placeholders, etc.).
 */
export const UNIFIED_CARD_LABEL_MAP = {
	...HOSPITAL_CARDS_LABELS,
	...OVERALL_CARDS_LABELS,
	...DECEASED_CARDS_LABELS,
	...ADMISSION_CARDS_LABELS,
	...COMMUNITY_CARD_LABELS,
};

const normalize = (value = '') => String(value).trim().toLowerCase();

/** Decode a few common HTML entities in saved titles (e.g. `&amp;` → `&`). */
export function decodeHtmlEntities(value = '') {
	let s = String(value);
	// Numeric entities first so we don't partially corrupt them.
	s = s.replace(/&#x([0-9a-f]{1,6});/gi, (_, hex) => {
		const code = parseInt(hex, 16);
		if (!Number.isFinite(code)) return _;
		try {
			return String.fromCodePoint(code);
		} catch {
			return _;
		}
	});
	s = s.replace(/&#(\d{1,7});/g, (_, dec) => {
		const code = parseInt(dec, 10);
		if (!Number.isFinite(code)) return _;
		try {
			return String.fromCodePoint(code);
		} catch {
			return _;
		}
	});
	const named = {
		'&amp;': '&',
		'&lt;': '<',
		'&gt;': '>',
		'&quot;': '"',
		'&#39;': "'",
		'&apos;': "'",
		'&nbsp;': ' ',
	};
	Object.entries(named).forEach(([entity, ch]) => {
		s = s.split(entity).join(ch);
		s = s.split(entity.toUpperCase()).join(ch);
	});
	return s;
}

/** Turn `hospitalizations` / `fooBar` into `Hospitalizations` / `Foo Bar`. */
function humanizeComponentKey(key) {
	if (key == null || key === '') return '';
	const raw = String(key).trim();
	if (!raw) return '';
	const spaced = raw
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim();
	if (!spaced) return '';
	return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
}
const stripCustomTabSuffix = (value = '') =>
	String(value).replace(/customtab$/i, '');
const stripCustomPrefix = (value = '') =>
	String(value).replace(/^custom_/i, '');

const createVariants = (value = '') => {
	const raw = normalize(value);
	const noSuffix = normalize(stripCustomTabSuffix(value));
	const noPrefix = normalize(stripCustomPrefix(value));
	const noPrefixNoSuffix = normalize(
		stripCustomTabSuffix(stripCustomPrefix(value))
	);
	return Array.from(
		new Set([raw, noSuffix, noPrefix, noPrefixNoSuffix].filter(Boolean))
	);
};

const findMatchingDynamicCard = (card, dynamicCards) => {
	if (!Array.isArray(dynamicCards) || dynamicCards.length === 0) return null;
	const cardIdVariants = createVariants(card?.id || '');
	if (cardIdVariants.length === 0) return null;
	return (
		dynamicCards.find((item) => {
			const itemVariants = [
				...createVariants(item?.accessor || ''),
				...createVariants(item?.key || ''),
				...createVariants(item?.id || ''),
				...createVariants(item?._id || ''),
			];
			if (itemVariants.length === 0) return false;
			const exactMatch = cardIdVariants.some((v) => itemVariants.includes(v));
			if (exactMatch) return true;
			return cardIdVariants.some((v) =>
				itemVariants.some((iv) => v.includes(iv) || iv.includes(v))
			);
		}) || null
	);
};

/**
 * Resolve the best user-facing title for a card object used in the drag/drop
 * grid. Walks several common shapes (dynamic cards, custom-tab cards, static
 * cards mapped by id, saved layout entries) and returns a cleaned-up string.
 * Returns an empty string if nothing reasonable can be derived.
 *
 * @param {object} card       Card object (usually `{...def, ...saved}`).
 * @param {object} [options]
 * @param {Array}  [options.dynamicCards] List of dynamic/custom-tab descriptors.
 * @param {object} [options.labelMap]     Optional extra id → label map that wins
 *                                        over the global UNIFIED_CARD_LABEL_MAP.
 */
export function getCardDisplayTitle(card, options = {}) {
	if (!card) return '';
	const { dynamicCards = [], labelMap = null } = options;

	const matchedDynamicCard = findMatchingDynamicCard(card, dynamicCards);
	const mappedFromProvidedMap =
		card?.id && labelMap ? labelMap[card.id] : null;
	const mappedFromUnified = card?.id ? UNIFIED_CARD_LABEL_MAP[card.id] : null;

	const inlineDynamicData = card?.data || null;

	const displayName =
		matchedDynamicCard?.title ||
		matchedDynamicCard?.label ||
		matchedDynamicCard?.tableLabel ||
		matchedDynamicCard?.question?.label ||
		matchedDynamicCard?.question?.title ||
		matchedDynamicCard?.question?.question ||
		inlineDynamicData?.title ||
		inlineDynamicData?.label ||
		inlineDynamicData?.tableLabel ||
		inlineDynamicData?.question?.label ||
		inlineDynamicData?.question?.title ||
		inlineDynamicData?.question?.question ||
		card?.label ||
		mappedFromProvidedMap ||
		mappedFromUnified ||
		card?.title ||
		card?.cardTitle ||
		card?.name ||
		card?.component ||
		card?.id ||
		'';

	const cleaned = String(displayName)
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

	return decodeHtmlEntities(cleaned).replace(/\s+/g, ' ').trim();
}

/**
 * Richer strings for layout-mode placeholder cards: decoded primary title plus a
 * short secondary line (category vs custom vs component) so tiles feel less empty.
 *
 * @param {object} card
 * @param {object} [options]  Same options as {@link getCardDisplayTitle}.
 * @returns {{ title: string, meta: string }}
 */
export function getCardPlaceholderMeta(card, options = {}) {
	const title = getCardDisplayTitle(card, options);
	if (!card) {
		return { title, meta: '' };
	}

	const dynamicCards = options.dynamicCards || [];
	const matchedDynamic = findMatchingDynamicCard(card, dynamicCards);
	const titleNorm = normalize(title);

	if (card.type === 'category') {
		return { title, meta: 'Category group' };
	}

	if (matchedDynamic) {
		return { title, meta: 'Custom field / tab' };
	}

	if (card.component) {
		const compLabel = humanizeComponentKey(card.component);
		const compNorm = normalize(compLabel);
		// Avoid repeating the same text as the main title when they resolve the same.
		if (compLabel && compNorm !== titleNorm) {
			return { title, meta: compLabel };
		}
	}

	if (card.id && normalize(card.id) !== titleNorm) {
		const idHint = String(card.id)
			.replace(/^custom_/i, '')
			.replace(/customtab$/i, '')
			.replace(/[_-]+/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
		if (idHint) {
			return { title, meta: decodeHtmlEntities(idHint) };
		}
	}

	return { title, meta: 'Dashboard card' };
}

export default getCardDisplayTitle;
